import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../../components/Pagination";

const SubGroup = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [allPosItems, setAllPosItems] = useState([]);
  const [subGroupOptions, setSubGroupOptions] = useState([]);
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("");
  const [allocatedItems, setAllocatedItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [selectedAvailableKeys, setSelectedAvailableKeys] = useState([]);
  const [selectedAllocatedKeys, setSelectedAllocatedKeys] = useState([]);
  const [movingRight, setMovingRight] = useState(false);
  const [movingLeft, setMovingLeft] = useState(false);
  const [initialAllocatedKeys, setInitialAllocatedKeys] = useState(new Set());
  const [initialAllocatedIdMap, setInitialAllocatedIdMap] = useState({});
  const [savingAllocation, setSavingAllocation] = useState(false);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch group data
  const fetchGroupData = async () => {
    setLoading(true);
    try {
      const GroupSearch = getUrlByOperation("GroupSearch") || 
        "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/Search";
      
      const response = await axios.post(
        GroupSearch,
        {
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setHasMore(response.data.data.length === dataLimit);
      } else {
        setData([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      setMessage("Failed to fetch group data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupData();
  }, [getUrlByOperation, token, userid, dataLimit, count]);

  const handleAddGroup = () => {
    navigate("/addgroup");
  };

  const handleUpdateGroup = (id) => {
    navigate(`/updategroup/${id}`);
  };

  const handleViewSubGroup = (id) => {
    navigate(`/subgroup/${id}`);
  };

  const buildPosKey = (item) => {
    if (!item) return "";
    if (item.posid !== undefined && item.posid !== null) return String(item.posid);
    const merchant = item.merchantid || "";
    const store = item.storeid || "";
    const posName = item.posname || "";
    return `${merchant}__${store}__${posName}`;
  };

  const formatPosLabel = (item) => {
    const merchant = item.merchantid || "-";
    const store = item.storeid || "-";
    const posName = item.posname || "-";
    return `${posName}, ${store}, ${merchant}`;
  };

  const fetchPosListForAllocation = async () => {
    const response = await axios.get(
      "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/SubUser/Get/NonAssign",
      {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
        },
      }
    );

    return Array.isArray(response?.data?.data) ? response.data.data : [];
  };

  const fetchSubGroupOptions = async (groupId) => {
    const SubGroupSearch =
      getUrlByOperation("SubGroupSearch") ||
      "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/SubUser/Search";

    const response = await axios.post(
      SubGroupSearch,
      {
        id: 0,
        groupid: groupId,
        ispageload: true,
        pagesize: 1000,
        currentpageno: 0,
        withoutpagination: true,
        pagedirection: "string",
      },
      {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      }
    );

    return Array.isArray(response?.data?.data) ? response.data.data : [];
  };

  const fetchAllocatedDataBySubGroup = async (subgroupId) => {
    if (!subgroupId) return [];

    const SubGroupDataSearch =
      getUrlByOperation("SubGroupDataSearch") ||
      "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/SubUser/Search";

    try {
      const response = await axios.post(
        SubGroupDataSearch,
        {
          subgroupid: Number(subgroupId),
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      return Array.isArray(response?.data?.data) ? response.data.data : [];
    } catch (error) {
      if (error?.response?.data?.suberrorcode === 54) {
        return [];
      }
      throw error;
    }
  };

  const splitAvailableAndAllocated = (allPos, allocatedPosData) => {
    const allocatedRecordMap = new Map();
    allocatedPosData.forEach((entry) => {
      allocatedRecordMap.set(buildPosKey(entry), entry);
    });

    const allocatedSet = new Set(allocatedPosData.map((entry) => buildPosKey(entry)));

    const allocated = allPos
      .filter((item) => allocatedSet.has(buildPosKey(item)))
      .map((item) => {
        const key = buildPosKey(item);
        const allocationRecord = allocatedRecordMap.get(key);
        return {
          ...item,
          subgroupDataId: allocationRecord?.id || null,
        };
      });
    const available = allPos.filter((item) => !allocatedSet.has(buildPosKey(item)));

    setAllocatedItems(allocated);
    setAvailableItems(available);
    setInitialAllocatedKeys(new Set(allocatedSet));
    const idMap = {};
    allocatedPosData.forEach((entry) => {
      idMap[buildPosKey(entry)] = entry.id;
    });
    setInitialAllocatedIdMap(idMap);
  };

  const handleOpenAllocationModal = async (groupItem) => {
    if (!token || !userid) {
      setMessage("Missing login session. Please login again.");
      return;
    }

    setSelectedGroup(groupItem);
    setShowAllocationModal(true);
    setLoadingAllocation(true);
    setSearchText("");
    setSelectedSubGroupId("");
    setAllocatedItems([]);
    setAvailableItems([]);

    try {
      const [posData, subgroupData] = await Promise.all([
        fetchPosListForAllocation(),
        fetchSubGroupOptions(groupItem.id),
      ]);

      setAllPosItems(posData);
      setSubGroupOptions(subgroupData);

      if (subgroupData.length > 0) {
        const firstSubgroup = subgroupData[0];
        const firstSubgroupId = firstSubgroup.id;
        setSelectedSubGroupId(String(firstSubgroupId));
        const allocatedData = await fetchAllocatedDataBySubGroup(firstSubgroupId);
        splitAvailableAndAllocated(posData, allocatedData);
      } else {
        setAllocatedItems([]);
        setAvailableItems(posData);
      }
    } catch (error) {
      console.error("Error loading allocation data:", error);
      setMessage("Failed to load allocation data");
    } finally {
      setLoadingAllocation(false);
    }
  };

  const handleSubGroupChange = async (e) => {
    const subgroupId = e.target.value;
    setSelectedSubGroupId(subgroupId);
    if (!subgroupId) {
      setAllocatedItems([]);
      setAvailableItems(allPosItems);
      return;
    }

    setLoadingAllocation(true);
    setSelectedAvailableKeys([]);
    setSelectedAllocatedKeys([]);
    try {
      const allocatedData = await fetchAllocatedDataBySubGroup(subgroupId);
      splitAvailableAndAllocated(allPosItems, allocatedData);
    } catch (error) {
      console.error("Error loading subgroup allocation:", error);
      setMessage("Failed to load subgroup allocation data");
    } finally {
      setLoadingAllocation(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        const GroupDelete = getUrlByOperation("GroupDelete") || 
          "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/Delete";
        
        await axios.post(
          GroupDelete,
          { id },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        setMessage("Group deleted successfully!");
        fetchGroupData(); // Refresh data after delete
      } catch (error) {
        console.error("Error deleting group:", error);
        setMessage("Failed to delete group");
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active 
        ? "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/SubUser/UpdateStatus/Active";
      
      const GroupActive = getUrlByOperation("GroupActive");
      const GroupDeactive = getUrlByOperation("GroupDeactive");
      
      const url = item.active 
        ? (GroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (GroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "Group deactivated successfully!" : "Group activated successfully!");
    } catch (error) {
      console.error("Error updating group status:", error);
      setMessage("Failed to update group status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Pagination navigation functions
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0); // Reset to first page when changing items per page
  };

  const goFirst = () => {
    if (count !== 0) setCount(0);
  };
  
  const goPrev = () => {
    if (count > 0) setCount(count - 1);
  };
  
  const goNext = () => {
    if (hasMore) setCount(count + 1);
  };

  const handlePageChange = (page) => {
    setCount(page - 1); // Convert 1-based page to 0-based index
  };

  const closeAllocationModal = () => {
    setShowAllocationModal(false);
    setSelectedGroup(null);
    setAllPosItems([]);
    setSubGroupOptions([]);
    setSelectedSubGroupId("");
    setAllocatedItems([]);
    setAvailableItems([]);
    setSearchText("");
    setSelectedAvailableKeys([]);
    setSelectedAllocatedKeys([]);
    setInitialAllocatedKeys(new Set());
    setInitialAllocatedIdMap({});
  };

  const toggleAvailableSelection = (item) => {
    const key = buildPosKey(item);
    setSelectedAvailableKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const toggleAllocatedSelection = (item) => {
    const key = buildPosKey(item);
    setSelectedAllocatedKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const handleMoveRight = async () => {
    if (selectedAvailableKeys.length === 0) return;

    setMovingRight(true);
    const selectedSet = new Set(selectedAvailableKeys);
    const movedItems = availableItems.filter((item) =>
      selectedSet.has(buildPosKey(item))
    );
    const remainingAvailable = availableItems.filter(
      (item) => !selectedSet.has(buildPosKey(item))
    );

    setAvailableItems(remainingAvailable);
    setAllocatedItems((prev) => [...prev, ...movedItems]);
    setSelectedAvailableKeys([]);
    setMovingRight(false);
  };

  const handleMoveLeft = () => {
    if (selectedAllocatedKeys.length === 0) return;
    setMovingLeft(true);
    const selectedKeySet = new Set(selectedAllocatedKeys);
    const moveBack = allocatedItems.filter((item) =>
      selectedKeySet.has(buildPosKey(item))
    );
    const remainingAllocated = allocatedItems.filter(
      (item) => !selectedKeySet.has(buildPosKey(item))
    );
    setAllocatedItems(remainingAllocated);
    setAvailableItems((prev) => [...prev, ...moveBack]);
    setSelectedAllocatedKeys([]);
    setMovingLeft(false);
  };

  const handleSaveAllocation = async () => {
    if (!selectedSubGroupId) {
      setMessage("Please select subgroup first");
      return;
    }

    const SubGroupDataInsert =
      getUrlByOperation("SubGroupDataInsert") ||
      "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/SubUser/Insert";
    const SubGroupDataDelete =
      getUrlByOperation("SubGroupDataDelete") ||
      "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/SubUser/Delete";

    const newItemsToInsert = allocatedItems.filter(
      (item) => !initialAllocatedKeys.has(buildPosKey(item))
    );
    const currentAllocatedKeySet = new Set(allocatedItems.map((item) => buildPosKey(item)));
    const keysToDelete = Array.from(initialAllocatedKeys).filter(
      (key) => !currentAllocatedKeySet.has(key)
    );
    const idsToDelete = keysToDelete
      .map((key) => initialAllocatedIdMap[key])
      .filter((id) => id !== undefined && id !== null);

    if (newItemsToInsert.length === 0 && idsToDelete.length === 0) {
      setMessage("No allocation changes to save");
      return;
    }

    setSavingAllocation(true);
    let insertSuccessCount = 0;
    let insertFailCount = 0;
    let deleteSuccessCount = 0;
    let deleteFailCount = 0;

    try {
      for (const item of newItemsToInsert) {
        try {
          await axios.post(
            SubGroupDataInsert,
            {
              subgroupid: Number(selectedSubGroupId),
              posname: item.posname || "",
              merchantid: item.merchantid || "",
              storeid: item.storeid || "",
              remark: item.remark || "",
              active: true,
            },
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json-patch+json",
                accept: "text/plain",
              },
            }
          );
          insertSuccessCount += 1;
        } catch (error) {
          console.error("Error saving allocation:", error);
          insertFailCount += 1;
        }
      }

      for (const id of idsToDelete) {
        try {
          await axios.post(
            SubGroupDataDelete,
            { id: Number(id) },
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json-patch+json",
                accept: "text/plain",
              },
            }
          );
          deleteSuccessCount += 1;
        } catch (error) {
          console.error("Error saving de-allocation:", error);
          deleteFailCount += 1;
        }
      }

      if (insertFailCount === 0 && deleteFailCount === 0) {
        setMessage("Allocation saved successfully!");
      } else {
        setMessage(
          `Insert: ${insertSuccessCount} success, ${insertFailCount} fail | Delete: ${deleteSuccessCount} success, ${deleteFailCount} fail`
        );
      }

      const refreshedAllocatedData = await fetchAllocatedDataBySubGroup(selectedSubGroupId);
      splitAvailableAndAllocated(allPosItems, refreshedAllocatedData);
      setSelectedAvailableKeys([]);
      setSelectedAllocatedKeys([]);
    } finally {
      setSavingAllocation(false);
    }
  };

  const searchMatcher = (item) => {
    if (!searchText.trim()) return true;
    const text = searchText.toLowerCase();
    const combined = `${item.posname || ""} ${item.storeid || ""} ${item.merchantid || ""}`.toLowerCase();
    return combined.includes(text);
  };

  const filteredAvailable = availableItems.filter(searchMatcher);
  const filteredAllocated = allocatedItems.filter(searchMatcher);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Groups</h1>
                <button 
                  type="button" 
                  className="btn btn-success" 
                  onClick={handleAddGroup}
                >
                  Add Group
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                        <thead className="thead-light">
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>Group Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "20%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "15%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => (
                              <tr key={index}>
                                <td className="text-center">{item.id}</td>
                                <td>
                                  <strong>{item.groupname || item.name || "-"}</strong>
                                </td>
                                <td>{item.description || item.remark || "-"}</td>
                                <td className="text-center">
                                  <div className="btn-group">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-info mr-1"
                                      onClick={() => handleViewSubGroup(item.id)}
                                      title="View SubGroup"
                                    >
                                      <i className="fas fa-list"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mr-1"
                                      onClick={() => handleUpdateGroup(item.id)}
                                      title="Edit Group"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDelete(item.id)}
                                      title="Delete Group"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-warning ml-1"
                                      onClick={() => handleOpenAllocationModal(item)}
                                      title="Allocation"
                                    >
                                      Allocation
                                    </button>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Switch
                                    isOn={item.active}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No groups found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <Pagination
                      currentPage={count + 1}
                      totalPages={hasMore ? count + 2 : count + 1}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={data.length + (hasMore ? dataLimit : 0)}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${
              message.includes("successfully") ? "alert-success" : "alert-danger"
            } d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}

      {message.includes("successfully") && <SuccessMessage message={message} />}
      {message.includes("Failed") && <FailedMessage message={message} />}

      {showAllocationModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Group Allocation</h5>
                <button type="button" className="close" onClick={closeAllocationModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Group</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedGroup?.id || ""}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Select SubGroup *</label>
                      <select
                        className="form-control"
                        value={selectedSubGroupId}
                        onChange={handleSubGroupChange}
                        disabled={loadingAllocation}
                      >
                        <option value="">Select SubGroup</option>
                        {subGroupOptions.map((subGroup) => (
                          <option key={subGroup.id} value={subGroup.id}>
                            {subGroup.name || `SubGroup ${subGroup.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search POS, Store or Merchant..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>

                {loadingAllocation ? (
                  <div className="text-center p-4">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {" "}Loading allocation...
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-md-5">
                      <h6>Available Items ({filteredAvailable.length})</h6>
                      <div className="border p-2" style={{ minHeight: "320px", maxHeight: "320px", overflowY: "auto" }}>
                        {filteredAvailable.length > 0 ? (
                          filteredAvailable.map((item, index) => (
                            <div 
                              key={`${buildPosKey(item)}-available-${index}`} 
                              className="border rounded p-2 mb-2 d-flex align-items-center"
                              style={{ cursor: 'pointer', backgroundColor: selectedAvailableKeys.includes(buildPosKey(item)) ? '#f0f8ff' : '#fff' }}
                              onClick={() => toggleAvailableSelection(item)}
                            >
                              <div className="form-check mr-3" style={{ pointerEvents: 'none' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{ position: 'static', margin: 0 }}
                                  checked={selectedAvailableKeys.includes(buildPosKey(item))}
                                  readOnly
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div style={{ fontWeight: '500', fontSize: '14px' }}>{formatPosLabel(item)}</div>
                                <small className="text-muted">ID: {item.posid || item.id || "-"}</small>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">No available items</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-2 d-flex justify-content-center align-items-center">
                      <div className="text-center">
                        <button
                          type="button"
                          className="btn btn-info mb-2"
                          disabled={selectedAvailableKeys.length === 0 || movingRight || !selectedSubGroupId}
                          onClick={handleMoveRight}
                        >
                          &rarr;
                        </button>
                        <br />
                        <button
                          type="button"
                          className="btn btn-info"
                          disabled={selectedAllocatedKeys.length === 0 || movingLeft}
                          onClick={handleMoveLeft}
                        >
                          &larr;
                        </button>
                      </div>
                    </div>

                    <div className="col-md-5">
                      <h6>Allocated Items ({filteredAllocated.length})</h6>
                      <div className="border p-2" style={{ minHeight: "320px", maxHeight: "320px", overflowY: "auto" }}>
                        {filteredAllocated.length > 0 ? (
                          filteredAllocated.map((item, index) => (
                            <div 
                              key={`${buildPosKey(item)}-allocated-${index}`} 
                              className="border rounded p-2 mb-2 d-flex align-items-center"
                              style={{ cursor: 'pointer', backgroundColor: selectedAllocatedKeys.includes(buildPosKey(item)) ? '#f0f8ff' : '#fff' }}
                              onClick={() => toggleAllocatedSelection(item)}
                            >
                              <div className="form-check mr-3" style={{ pointerEvents: 'none' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{ position: 'static', margin: 0 }}
                                  checked={selectedAllocatedKeys.includes(buildPosKey(item))}
                                  readOnly
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div style={{ fontWeight: '500', fontSize: '14px' }}>{formatPosLabel(item)}</div>
                                <small className="text-muted">ID: {item.posid || item.id || "-"}</small>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-muted">No allocated items</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeAllocationModal}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleSaveAllocation}
                  disabled={savingAllocation || !selectedSubGroupId}
                >
                  {savingAllocation ? "Saving..." : "Save Allocation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}